// src/pages/Dashboard.js
import { Card, Tag, ProgressBar, PageHeader } from "../components/UI";
import { Avatar } from "../components/UI";

const today = () => new Date().toISOString().split("T")[0];
const fmt = d => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

export default function Dashboard({ employees, attendance }) {
  const cur  = employees.filter(e => e.status === "current").length;
  const past = employees.filter(e => e.status === "past").length;
  const todayAtt = attendance.find(a => a.date === today());
  const presentToday = todayAtt ? todayAtt.records.filter(r => r.status === "present").length : 0;
  const depts = [...new Set(employees.map(e => e.dept).filter(Boolean))];

  const StatCard = ({ label, value, sub, color = "var(--rust)" }) => (
    <Card style={{ flex: 1, minWidth: 150 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: "var(--slate-light)", textTransform: "uppercase", marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 38, fontWeight: 800, color, fontFamily: "'Syne',sans-serif", lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "var(--slate-light)", marginTop: 6 }}>{sub}</div>}
    </Card>
  );

  return (
    <div style={{ animation: "fadeUp .35s ease" }}>
      <PageHeader title="Dashboard" sub="Overview of Radhakrishna Academy staff" />

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 28 }}>
        <StatCard label="Current Staff"    value={cur}  sub={`${past} past employee${past !== 1 ? "s" : ""}`} color="var(--slate)" />
        <StatCard label="Departments"      value={depts.length} sub="Active divisions" color="var(--rust)" />
        <StatCard label="Present Today"    value={presentToday} sub={`of ${cur} current staff`} color="var(--success)" />
        <StatCard label="Total (All Time)" value={employees.length} sub="Including past" color="#b87a00" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
        {/* Dept breakdown */}
        <Card>
          <h4 style={{ marginBottom: 18, fontSize: 15, fontFamily: "'Syne',sans-serif" }}>Department Breakdown</h4>
          {depts.length === 0 && <p style={{ fontSize: 13, color: "var(--slate-light)" }}>No departments yet.</p>}
          {depts.map(dept => {
            const count = employees.filter(e => e.dept === dept && e.status === "current").length;
            return (
              <div key={dept} style={{ marginBottom: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 5 }}>
                  <span>{dept}</span>
                  <span style={{ fontWeight: 600 }}>{count}</span>
                </div>
                <ProgressBar value={cur > 0 ? (count / cur) * 100 : 0} color="var(--rust)" />
              </div>
            );
          })}
        </Card>

        {/* Recent employees */}
        <Card>
          <h4 style={{ marginBottom: 18, fontSize: 15, fontFamily: "'Syne',sans-serif" }}>Recent Employees</h4>
          {employees.length === 0 && <p style={{ fontSize: 13, color: "var(--slate-light)" }}>No employees added yet.</p>}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {employees.slice(0, 6).map(e => (
              <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Avatar name={e.name} size={34} photo={e.photoURL} />
                <div style={{ flex: 1, overflow: "hidden" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.name}</div>
                  <div style={{ fontSize: 11, color: "var(--slate-light)" }}>{e.role || "—"}</div>
                </div>
                <Tag label={e.status === "current" ? "Active" : "Past"} color={e.status === "current" ? "var(--success)" : "var(--slate-light)"} />
              </div>
            ))}
          </div>
        </Card>

        {/* Attendance history */}
        <Card style={{ gridColumn: "1 / -1" }}>
          <h4 style={{ marginBottom: 18, fontSize: 15, fontFamily: "'Syne',sans-serif" }}>Recent Attendance</h4>
          {attendance.length === 0 && <p style={{ fontSize: 13, color: "var(--slate-light)" }}>No attendance recorded yet.</p>}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[...attendance].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 5).map(a => {
              const p  = a.records.filter(r => r.status === "present").length;
              const ab = a.records.filter(r => r.status === "absent").length;
              const lt = a.records.filter(r => r.status === "late").length;
              const pct = a.records.length > 0 ? Math.round((p / a.records.length) * 100) : 0;
              return (
                <div key={a.date} style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                  <div style={{ fontWeight: 600, fontSize: 14, minWidth: 110 }}>{fmt(a.date)}</div>
                  <div style={{ flex: 1, minWidth: 80 }}><ProgressBar value={pct} color="var(--success)" /></div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <Tag label={`✓ ${p}`} color="var(--success)" />
                    <Tag label={`✗ ${ab}`} color="var(--danger)" />
                    {lt > 0 && <Tag label={`⌚ ${lt}`} color="var(--warn)" />}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}
